// Orange banner shown at the top of every dashboard page when a
// super_admin is impersonating an org owner. Big obvious "Return to
// super admin" button because it's easy to forget you're logged in as
// someone else after a support call.
import { AlertTriangle, LogOut } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export function ImpersonationBanner({ user }) {
  const [ending, setEnding] = useState(false);
  if (!user?.impersonated_by) return null;

  const endSession = async () => {
    if (ending) return;
    setEnding(true);
    try {
      await api.post("/platform/impersonate/end");
      // Swap the localStorage tokens back
      const sa = localStorage.getItem("fl_sa_token");
      if (sa) {
        localStorage.setItem("fl_token", sa);
        localStorage.removeItem("fl_sa_token");
      } else {
        localStorage.removeItem("fl_token");
      }
      toast.success("Restored super admin session");
      // Hard reload to /app/platform so the super admin lands back on their console
      window.location.replace("/app/platform");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't end session — try logging out and back in");
      setEnding(false);
    }
  };

  const targetOrg = user.org?.name || "this org";
  const targetName = user.name || user.email;
  const saName = user.impersonated_by.name || user.impersonated_by.email;

  return (
    <div
      className="sticky top-0 z-[60] bg-[#FF5F15] text-white border-b-4 border-black"
      data-testid="impersonation-banner"
    >
      <div className="max-w-[1600px] mx-auto flex items-center gap-3 px-4 py-2.5">
        <div className="w-9 h-9 rounded-full bg-black flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-[#FF5F15]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black uppercase tracking-tight text-sm sm:text-base leading-tight">
            Impersonating {targetName} @ {targetOrg}
          </div>
          <div className="text-[11px] sm:text-xs font-mono opacity-90 truncate">
            Signed in via support-session by <b>{saName}</b> — every action is audited
          </div>
        </div>
        <button
          type="button"
          onClick={endSession}
          disabled={ending}
          className="shrink-0 inline-flex items-center gap-1.5 bg-black hover:bg-zinc-800 disabled:bg-zinc-700 text-white font-display font-black uppercase tracking-wider text-[11px] sm:text-xs px-3 sm:px-4 py-2 rounded-sm transition-colors"
          data-testid="impersonation-return-btn"
        >
          <LogOut className="w-3.5 h-3.5" />
          {ending ? "Returning…" : "Return to super admin"}
        </button>
      </div>
    </div>
  );
}
